import { useTranslation } from "react-i18next";
import { URLS } from "../../../../services/urls";
import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../../../../services/axios";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { confirmDeletion } from "../../../../components/dletion";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import LanguageCard from "./components/LanguageCard";
import { ErrorMessage } from "@hookform/error-message";

const ManageLanguages = ({ handleNext, handlePrev }) => {
  const { t }      = useTranslation();
  const { action } = useParams();

  const [languages, setLanguages]     = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [toEdit, setToEdit]           = useState(null);
  const [loading, setLoading]         = useState(false);
  const [description, setDescription] = useState("");

  const { register, handleSubmit, reset, formState: { errors } } = useForm({});
  const formRef = useRef(null);

  // ─── Fetch languages, filter client-side by profile ──────────────────────
  const loadLanguages = useCallback(() => {
    if (!action || action === "create") return;
    setFetchLoading(true);
    API.get(URLS.USERS.PROFILE.details(action))
      .then(({ data }) => {
        setLanguages(data.languages || []);
        setFetchLoading(false);
      })
      .catch(() => setFetchLoading(false));
  }, [action]);

  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  // ─── Edit ──────────────────────────────────────────────────────────────────
  const handleChangeLanguage = (lang) => {
    setToEdit(lang);
    reset({ designation: lang.designation, level: lang.level });
    setDescription(lang.description || "");
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteLanguage = (lang) => {
    const onSuccess = () =>
      setLanguages((prev) => prev.filter((l) => l.id !== lang.id));
    confirmDeletion(URLS.USERS.PROFILE.LANGUAGES.update(lang.id), t, onSuccess);
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = (data) => {
    setLoading(true);
    const fd = new FormData();
    fd.append("designation", data.designation);
    fd.append("level",       data.level);
    fd.append("profile",     action);
    fd.append("description", description);

    if (!toEdit) {
      API.post(URLS.USERS.PROFILE.LANGUAGES.create, fd)
        .then(({ data: resp }) => {
          setLoading(false);
          reset();
          setDescription("");
          toast.success(t("textLanguageAdded"));
          setLanguages((prev) => [resp, ...prev]);
        })
        .catch((err) => {
          toast.error(
            err?.response?.data?.detail ??
            err?.response?.data?.messagerror ??
            t("textLanguageAddingError")
          );
          setLoading(false);
        });
    } else {
      API.patch(URLS.USERS.PROFILE.LANGUAGES.update(toEdit.id), fd)
        .then(({ data: resp }) => {
          setLoading(false);
          toast.success(t("languageEdited"));
          setLanguages((prev) =>
            prev.map((l) => (l.id === toEdit.id ? resp : l))
          );
          setToEdit(null);
          reset();
          setDescription("");
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="panel-white mb-30" id="languages">
      <div className="box-padding">
        <h5 className="icon-edu">{t("textLanguages")}</h5>
        <div className="row mt-30">
          <div className="col-lg-9">
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              <div className="row">
                {/* Language name */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("textLangName")} <span className="text-primary">*</span>
                    </label>
                    <input
                      {...register("designation", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="text"
                      placeholder={t("textLangName")}
                    />
                    <ErrorMessage errors={errors} name="designation"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>

                {/* Level */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("textLevel")} <span className="text-primary">*</span>
                    </label>
                    <select
                      className="form-control"
                      {...register("level", { required: t("textFormFieldRequired") })}
                    >
                      <option value="">{t("textChoose")}</option>
                      <option value="beginner">{t("textBeginner")}</option>
                      <option value="intermediate">{t("Intermediare")}</option>
                      <option value="advanced">{t("textAdvanced")}</option>
                    </select>
                    <ErrorMessage errors={errors} name="level"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="col-lg-12">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("textDescription")}</label>
                    <ReactQuill
                      theme="snow"
                      value={description}
                      onChange={setDescription}
                      style={{ height: "180px", marginBottom: "50px" }}
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="col-lg-12">
                  <div className="form-group mt-10 d-flex">
                    <button type="submit" className="btn btn-default btn-brand icon-tick ml-10">
                      {!toEdit ? t("textAddLanguage") : t("textEditLanguage")}
                      {loading && <span className="spinner-border spinner-border-sm ml-10" role="status" />}
                    </button>
                    {toEdit && (
                      <button
                        type="button"
                        className="btn btn-secondary ml-10"
                        onClick={() => { setToEdit(null); reset(); setDescription(""); }}
                      >
                        {t("deleteActionCancel")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* List */}
            {fetchLoading ? (
              <div className="d-flex mt-30">
                <div className="spinner-border m-auto spinner-border-sm" role="status" />
              </div>
            ) : (
              <div className="box-timeline mt-50">
                {languages.map((lang) => (
                  <LanguageCard
                    key={lang.id}
                    handleChange={handleChangeLanguage}
                    handleDelete={handleDeleteLanguage}
                    language={lang}
                  />
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className="col-lg-12 mt-3">
              <div className="form-group mt-10 d-flex">
                <button type="button" onClick={handlePrev} className="btn btn-secondary btn-brand line-height-24">
                  {t("textPrevious")}
                </button>
                <button type="button" onClick={handleNext} className="btn btn-default btn-brand ml-auto">
                  {t("textNext")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageLanguages;
